import { useState, useEffect } from 'react';

const useRestaurantInfo = () => {
  const [info, setInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_BASE = process.env.REACT_APP_USE_CMS === 'true' 
    ? process.env.REACT_APP_API_URL || 'http://localhost:3001/api'
    : null;

  // Default fallback values
  const defaultInfo = {
    restaurant_name: 'Cioconat',
    phone: '+962-(6)-541-2484',
    email: 'info@cioconatcafe.com',
    address: 'Al Shab St 253, Dabouq, Amman, Jordan',
    address_short: 'Al-Sha\'b St, Dabouq, Amman 11822, JO',
    hours_display: '9.00 am - 1.00 am',
    breakfast_hours: 'Monday to Sunday 9.00 am - 1.00pm',
    contact_email_alt: 'info@cioconatlounge.com',
    google_maps_embed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3383.9848299171895!2d35.818368299999996!3d31.988426699999998!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x151ca225393a440f%3A0xe36d80755301eec2!2sCioconat%20Cafe&#39;%20%26%20Resto!5e0!3m2!1sen!2sin!4v1688646933254!5m2!1sen!2sin',
    newsletter_title: 'Get News & Offers',
    newsletter_text: 'enjoy mouthwatering surprises and tempting offers delivered straight to your inbox!',
    booking_text: 'Booking request',
    visit_us_text: 'Visit Us',
    daily_text: 'Daily',
    location_text: 'Location',
    social_media: {
      facebook: 'https://www.facebook.com/profile.php?id=100093252044521',
      instagram: 'https://www.instagram.com/cioconatcaferestaurant/',
      twitter: '',
      google_maps: 'https://goo.gl/maps/oDf6LY5eJfupbd196'
    }
  };

  useEffect(() => {
    const fetchInfo = async () => {
      // If CMS is not enabled, use default values
      if (!API_BASE) {
        setInfo(defaultInfo);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(`${API_BASE}/restaurant-info`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Transform CMS data to a flat structure
        const transformedInfo = {};
        Object.keys(data.data).forEach(key => {
          const item = data.data[key];
          if (item.data_type === 'json') {
            try {
              transformedInfo[key] = typeof item.value === 'string' 
                ? JSON.parse(item.value) 
                : item.value;
            } catch (e) {
              transformedInfo[key] = item.value;
            }
          } else {
            transformedInfo[key] = item.value;
          }
        });

        // Merge with defaults to ensure all fields are available
        setInfo({ ...defaultInfo, ...transformedInfo });
        setError(null);
      } catch (err) {
        console.warn('Failed to fetch restaurant info:', err);
        setError(err);
        // Fallback to default values on error
        setInfo(defaultInfo);
      } finally {
        setLoading(false);
      }
    };

    fetchInfo();
  }, [API_BASE]);

  // Helper function to get a specific info value with fallback
  const getInfo = (key, fallback = '') => {
    return info[key] || defaultInfo[key] || fallback;
  };

  // Helper function to get social media link
  const getSocialMedia = (platform) => {
    const socialMedia = info.social_media || defaultInfo.social_media;
    return socialMedia[platform] || '';
  };

  // Helper function to format phone for tel: links
  const getPhoneLink = () => {
    const phone = getInfo('phone');
    return `tel:${phone.replace(/\s/g, '')}`;
  };

  // Helper function to format email for mailto: links
  const getEmailLink = (emailKey = 'email') => {
    const email = getInfo(emailKey);
    return `mailto:${email}`;
  };

  return {
    info,
    loading,
    error,
    getInfo,
    getSocialMedia,
    getPhoneLink,
    getEmailLink,
    isUsingCMS: !!API_BASE
  };
};

export default useRestaurantInfo;